import { useMemo, useState } from 'react'
import { IIbanStatus } from '~/@api/generated-api-types-user/Api'
import { Button } from '~/shadcn/ui/button/button'
import { Card, CardContent, CardHeader } from '~/shadcn/ui/card'
import { DeleteIcon } from '~/@mid_ui/icons/DeleteIcon'
import { LabelValueItem } from '~/@mid_ui/label-value/LabelValueItem'
import { HeadingThree } from '~/@mid_ui/typography/HeadingThree'
import { Text } from '~/@mid_ui/typography/Text'
import type { UserWithMeta } from '~/@biz_account/useUser/types'
import { DeleteBankAccountDialog } from './DeleteBankAccountDialog'

type Props = {
  user: UserWithMeta
  onAccountDeleted: () => void
}
function BankAccountList({ user, onAccountDeleted }: Props) {
  const [bankAccountIbanToDelete, setBankAccountIbanToDelete] = useState<
    string | null
  >(null)

  const bankAccounts = useMemo(
    () =>
      (user.profile.bankAccounts || []).filter(
        (account) => account.statusId !== IIbanStatus.Deleted
      ),
    [user.profile.bankAccounts]
  )

  function handleCloseDeleteBankAccountDialog() {
    setBankAccountIbanToDelete(null)
  }

  return (
    <div className="flex flex-row gap-4 flex-wrap">
      {bankAccounts.map((account, i) => (
        <Card
          key={account.iban}
          className="flex flex-col w-full md:w-fit gap-5 border-border">
          <CardHeader className="flex flex-row items-center justify-between px-5">
            <HeadingThree>
              {getBankAccountHeadingLabel(i + 1, account.iban.slice(-8))}
            </HeadingThree>
            <Button
              variant="ghost"
              className="w-6 h-6"
              onClick={() => setBankAccountIbanToDelete(account.iban)}>
              <DeleteIcon />
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 px-5">
            {getBankAccountFields(user.profile, account.iban).map((item) => (
              <LabelValueItem
                key={item.label}
                label={item.label}
                value={item.value}
                labelClassName="w-auto text-sm uppercase"
                className="my-2 flex sm:flex-col justify-start items-start sm:items-start font-semibold"
                contentClassName=""
              />
            ))}
          </CardContent>
        </Card>
      ))}
      {bankAccountIbanToDelete && (
        <DeleteBankAccountDialog
          bankAccountIbanToDelete={bankAccountIbanToDelete}
          onClose={handleCloseDeleteBankAccountDialog}
          onSuccess={() => {
            handleCloseDeleteBankAccountDialog()
            onAccountDeleted()
          }}
        />
      )}
    </div>
  )
}

function getBankAccountHeadingLabel(order: number, number: string) {
  return `${order}. účet ${number}`
}

function getBankAccountFields(
  userProfile: UserWithMeta['profile'],
  iban: string
) {
  return [
    {
      label: 'Typ účtu',
      value: (
        <Text>
          {userProfile.isCompany ? 'Právnická osoba' : 'Fyzická osoba'}
        </Text>
      ),
    },
    { label: 'Predvolená mena', value: 'EUR' },
    {
      label: 'IBAN kód vášho bankového účtu',
      value: iban.replace(/.{4}(?=.)/g, '$&\xa0'),
    },
  ]
}

export { BankAccountList }
